import {
  Divider,
  DropdownItem,
  DropdownMenu,
  FormControlSizes,
  IconButton,
  IconName,
  isMultileg,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useMixpanel,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback } from 'react';
import type { MultilegComboType } from '../MultilegCombo/enums';
import { CloneSymbolDropdownItem } from '../SymbolActionsMenu/CloneSymbolDropdownItem';
import { CreateMultilegDropdownItem } from '../SymbolActionsMenu/CreateMultilegDropdownItem';
import { FavoriteSymbolDropdownItem } from '../SymbolActionsMenu/FavoriteSymbolDropdownItem';

interface MarketDataCardMenuProps {
  security: Security | undefined;
  onOpenSettings: () => void;
  onOpenDeepDive: () => void;
  onDeleteCard: () => void;
  onCreateMultileg?: (multilegType: MultilegComboType) => void;
  onEditSymbol?: () => void;
  onCloneSymbol?: (security: Security) => void;
  onOpenMarketSelector?: () => void;
}

export const MarketDataCardMenu = memo(function MarketDataCardMenu({
  security,
  onOpenSettings,
  onOpenDeepDive,
  onDeleteCard,
  onCreateMultileg,
  onEditSymbol,
  onCloneSymbol,
  onOpenMarketSelector,
}: MarketDataCardMenuProps) {
  const isMultilegSecurity = isMultileg(security);
  const mixpanel = useMixpanel();

  const handleMenuOpen = useCallback(() => {
    mixpanel.track(MixpanelEvent.OpenSymbolMenu, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCardHeader,
    });
  }, [mixpanel]);

  return (
    <DropdownMenu
      onOpen={handleMenuOpen}
      triggerElement={
        <IconButton
          data-testid="market-data-card-menu"
          ghost
          size={FormControlSizes.Small}
          dim
          icon={IconName.DotsVertical}
        />
      }
      trigger="click"
      placement="bottom-end"
      dropdownProps={{ style: { width: '220px' } }}
    >
      <DropdownItem startIcon={IconName.Cog} onClick={onOpenSettings}>
        Edit Card
      </DropdownItem>

      {!isMultilegSecurity && onOpenMarketSelector ? (
        <DropdownItem startIcon={IconName.Scale} onClick={onOpenMarketSelector}>
          Market Selector
        </DropdownItem>
      ) : null}

      <FavoriteSymbolDropdownItem security={security} source={MixpanelEventSource.MarketCardHeader} />

      <DropdownItem startIcon={IconName.Deepdive} onClick={onOpenDeepDive}>
        Open DeepDive
      </DropdownItem>

      {onCreateMultileg && <CreateMultilegDropdownItem security={security} onCreateMultileg={onCreateMultileg} />}

      {isMultilegSecurity && onEditSymbol && (
        <DropdownItem startIcon={IconName.Pencil} onClick={onEditSymbol}>
          Edit Symbol
        </DropdownItem>
      )}
      <CloneSymbolDropdownItem security={security} onCloneSymbol={onCloneSymbol} />

      <Divider />
      <DropdownItem startIcon={IconName.Trash} onClick={onDeleteCard}>
        Delete Card
      </DropdownItem>
    </DropdownMenu>
  );
});
